/**
 * Delivery state of the quote emails of a work order (#3787): one history per
 * send row, since the email carries the send's id. WhatsApp sends have no
 * email to track and are left out.
 *
 * The send list arrives whole; the histories are revealed a few at a time
 * ("show more"), each costing one request, so no send is out of reach and a
 * work order with many sends does not fan out all at once.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { api } from '@/lib/api';
import { EmailDeliveryHistory } from '@/components/email/EmailDeliveryHistory';
import { useRevealedCount } from '@/components/email/useRevealedCount';
import { queryKeys } from '@/hooks/queryKeys';
import { formatDateTime } from '@/utils/locale';

interface QuoteSend {
  id: string;
  sentAt: string;
  sentVia: string;
  recipient: string;
}

export function QuoteSendDeliveries({ workOrderId }: { workOrderId: string }) {
  const { t } = useTranslation('common');
  const sends = useQuery({
    queryKey: queryKeys.workOrderQuoteSends(workOrderId),
    queryFn: () => api.get<{ data: QuoteSend[] }>(`/work-orders/${workOrderId}/quote-sends`),
  });
  const emailSends = (Array.isArray(sends.data?.data) ? sends.data.data : []).filter(
    (send) => send.sentVia === 'email',
  );
  const { visible, hasMore, showMore } = useRevealedCount(workOrderId, emailSends.length);

  if (sends.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void sends.refetch()}>
            {t('emailDelivery.retry')}
          </Button>
        }
      >
        {t('emailDelivery.sendsLoadError')}
      </Alert>
    );
  }
  if (sends.isPending) {
    return (
      <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} aria-hidden="true" />
        <Typography variant="body2">{t('emailDelivery.loading')}</Typography>
      </Box>
    );
  }
  if (emailSends.length === 0) return null;
  return (
    <Stack spacing={2} data-testid="quote-send-deliveries">
      {emailSends.slice(0, visible).map((send) => (
        <div key={send.id}>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(send.sentAt)} · {send.recipient}
          </Typography>
          <EmailDeliveryHistory entityType="quote" entityId={send.id} title={t('emailDelivery.quoteTitle')} />
        </div>
      ))}
      {hasMore ? (
        <Box>
          <Button size="small" onClick={showMore}>
            {t('emailDelivery.showMoreSends')}
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
}
